import React, { useMemo, useState } from 'react';
import LineChart from '../../../../Components/Chart/LineChart';
import { ILaboratoryResultListItem } from './LaboratoryResultItemsList';
import { getMomentDate } from '../../../../Utils/dates.util';
import { useAppContext } from '../../../../Services/AppContext';
import useTranslation from '../../../../Hooks/useTranslation';
import moment from 'moment';

interface Props {
	selectedItem: ILaboratoryResultListItem;
	items: ILaboratoryResultListItem[];
}

type chartDatasetData = string | number | null;

interface ChartDataset {
	label: string;
	data: chartDatasetData[];
	borderColor?: string;
	backgroundColor?: string;
	pointRadius?: number;
	borderDash?: number[];
}

const LaboratoryResultChart: React.FC<Props> = ({ selectedItem, items }) => {
	const [isLoading, setIsLoading] = useState(false);
	const { dateFormatMoment } = useAppContext();
	const { t } = useTranslation();

	const data: { labels: string[]; datasets: ChartDataset[] } = useMemo(() => {
		const chartLabels: string[] = [];
		const chartData: number[] = [];
		const upperReferenceValueData: number[] = [
			selectedItem.upperReferenceValue,
			selectedItem.upperReferenceValue,
		];
		const lowerReferenceValueData: number[] = [
			selectedItem.lowerReferenceValue,
			selectedItem.lowerReferenceValue,
		];

		items
			.filter(f => f.name === selectedItem.name)
			.sort((a, b) => moment(a.createdAt).diff(moment(b.createdAt)))
			.forEach(item => {
				chartLabels.push(
					getMomentDate(item.createdAt).format(dateFormatMoment),
				);
				chartData.push(item.result);
				upperReferenceValueData.push(selectedItem.upperReferenceValue);
				lowerReferenceValueData.push(selectedItem.lowerReferenceValue);
			});

		return {
			labels: ['', ...chartLabels, ''],
			datasets: [
				{
					label: selectedItem.name,
					data: [null, ...chartData, null],
					borderColor: 'rgb(88,94,172)',
					backgroundColor: 'rgb(88,94,172)',
					pointRadius: 3,
				},
				{
					label: t('component.laboratoryResultChart.upperRef') ?? '',
					data: upperReferenceValueData,
					borderDash: [8, 10],
					borderColor: 'red',
					backgroundColor: 'red',
					pointRadius: 0,
				},
				{
					label: t('component.laboratoryResultChart.lowerRef') ?? '',
					data: lowerReferenceValueData,
					borderDash: [8, 10],
					borderColor: 'lightBlue',
					backgroundColor: 'lightBlue',
					pointRadius: 0,
				},
			],
		};
	}, [selectedItem, items]);

	return (
		<LineChart
			data={data}
			isLoading={isLoading}
			options={{
				scales: {
					y: {
						beginAtZero: true,
						ticks: {
							fontSize: 18,
						},
					},
				},
				responsive: true,
				plugins: {
					legend: {
						position: 'right',
						labels: {
							usePointStyle: true,
						},
					},
				},
				maintainAspectRatio: false,
			}}
		/>
	);
};

export default LaboratoryResultChart;
